<template>
  <div
    v-if="invited.includes(user.id) && !loading"
    class="mr-4 d-flex align-center"
  >
    <DpplTooltip
      v-if="inviteErrors[user.id]"
      bottom
      color="white"
    >
      <template #activator>
        <a
          class="text-error"
          v-on="on"
        >
          Invite Failed
        </a>
      </template>
      <div class="text-left">
        <strong> Reason for failure: </strong> {{ inviteErrors[user.id] }}
      </div>
    </DpplTooltip>
    <a
      v-else
      class="text-success"
    >
      Success!
    </a>
  </div>
  <div
    v-else-if="status !== 'active' && inviteType === 'invite-onboard'"
    class="d-flex align-center"
  >
    <DpplTooltip
      bottom
      color="white"
    >
      <template #activator>
        <a
          v-if="user.invitedAt"
          class="text-success mr-4"
          v-on="on"
        >
          Invited
        </a>
      </template>
      Invited {{ relativeInviteDate }}
    </DpplTooltip>
  </div>
  <DpplNewButton
    v-if="inviteType === 'invite-onboard'"
    color="primary"
    height="30px"
    :variant="inviteOnboardingText === 'Resend Invite' ? 'outlined' : 'elevated'"
    :disabled="loading"
    @click="inviteSingleUser"
  >
    {{ inviteOnboardingText }}
  </DpplNewButton>
  <DpplNewButton
    v-else-if="showDAInvite"
    color="primary"
    variant="outlined"
    height="30px"
    :disabled="loading"
    @click="inviteSingleUser"
  >
    Invite to Datapeople Anywhere
  </DpplNewButton>
  <div
    v-if="showDALoader"
    class="da-loader-container"
  >
    <DpplPageLoader
      :container-height="60"
      :size="15"
      :width="2"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { formatRelative } from 'date-fns';
import { useAppAuthData } from '@/stores/app/authData';
import DpplTooltip from '@/components/datapeople/DpplTooltip.vue';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import DpplPageLoader from '@/components/datapeople/DpplPageLoader.vue';
import { storeToRefs } from 'pinia';

const emits = defineEmits(['invite-onboard', 'invite-da']);

const props = defineProps({
  user: { type: Object, default: () => ({}) },
  disabled: { type: Boolean, default: false },
  inviteLoading: { type: Array, default: () => [] },
  invited: { type: Array, default: () => [] },
  inviteErrors: { type: Object, default: () => ({}) },
  status: { type: String, default: 'inactive' },
  daInstalled: { type: Boolean, default: false },
});

// Stores
const { authData } = storeToRefs(useAppAuthData());

// Computed
const loading = computed(
  () => props.inviteLoading.some((id) => id === props.user.id),
);

const datapeopleAnywhereEnabled = computed(() => authData.value.extensionJobsEnabled);

const inviteType = computed(() => {
  if (props.status === 'inactive' && !props.disabled) return 'invite-onboard';

  if (
    props.status === 'active'
    && !props.daInstalled
    && datapeopleAnywhereEnabled.value
  ) return 'invite-da';

  return '';
});

const inviteOnboardingText = computed(() => ((props.invited.includes(props.user.id)
      || props.user.invitedAt)
  ? 'Resend Invite'
  : 'Onboard onto Datapeople'));

const relativeInviteDate = computed(() => {
  if (props.user.invitedAt) {
    return formatRelative(new Date(props.user.invitedAt), Date.now());
  } return null;
});

const showDAInvite = computed(
  () => inviteType.value === 'invite-da' && !props.invited.includes(props.user.id),
);

const showDALoader = computed(
  () => inviteType.value === 'invite-da' && loading.value && props.invited.includes(props.user.id),
);

// Methods
const inviteSingleUser = () => {
  const { id } = props.user || {};
  if (inviteType.value === 'invite-onboard') {
    emits('invite-onboard', id);
  }

  if (inviteType.value === 'invite-da') {
    emits('invite-da', id);
  }
};
</script>

<style scoped lang="less">
.da-loader-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
}
</style>
